import { normalizeLeadStatus } from '@/lib/leadStatus';
// Lead Kategorilendirme Utility
// AILeadScout.jsx'ten çıkarılan fonksiyonlar

/**
 * Lead skorunu hesaplar (0-100 arası) - V2
 * Sipariş geçmişi, recency decay, sektör değeri ve CLV sinyalleri ile
 * @param {Object} lead - Lead objesi
 * @param {Array} existingLeads - Mevcut lead listesi
 * @param {Array} orders - Sipariş geçmişi
 * @param {Array} tasks - Görev geçmişi
 * @returns {number} 0-100 arası skor
 */
export function calculateLeadScore(lead, existingLeads = [], orders = [], tasks = []) {
  let score = 30; // Başlangıç skoru (düşürüldü, daha fazla sinyal ağırlığı)

  // ── 1. SEKTÖR DEĞERİ (0-15 puan) ──
  const brancheValues = {
    'Hotel': 15, 'Industrie': 15, 'Italienische Küche': 12,
    'Asiatische Küche': 10, 'Indische Küche': 10,
    'Burger': 8, 'Döner': 7, 'Bowl/Smoothies': 6,
  };
  score += brancheValues[lead.branche] || 5;

  // ── 2. VERİ KALİTESİ (0-15 puan) ──
  if (lead.strasse && lead.plz && lead.ort) {
    score += /\d+/.test(lead.strasse) ? 8 : 4;
  }
  if (lead.telefon) score += 4;
  if (lead.email) score += 3;

  // ── 3. SİPARİŞ GEÇMİŞİ (0-25 puan) ──
  if (orders.length > 0) {
    // Sipariş sayısı bonusu (max 10)
    score += Math.min(orders.length * 3, 10);

    // Toplam sipariş değeri bonusu (max 10)
    const totalOrderValue = orders.reduce((sum, o) => sum + (o.total || 0), 0);
    if (totalOrderValue >= 5000) score += 10;
    else if (totalOrderValue >= 2000) score += 7;
    else if (totalOrderValue >= 500) score += 4;
    else if (totalOrderValue > 0) score += 2;

    // Son sipariş yakınlığı bonusu (max 5)
    const lastOrderDate = orders
      .map(o => o.created_date ? new Date(o.created_date) : null)
      .filter(Boolean)
      .sort((a, b) => b - a)[0];
    if (lastOrderDate) {
      const daysSinceOrder = (Date.now() - lastOrderDate.getTime()) / (1000 * 60 * 60 * 24);
      if (daysSinceOrder <= 7) score += 5;
      else if (daysSinceOrder <= 30) score += 3;
      else if (daysSinceOrder <= 90) score += 1;
    }
  }

  // ── 4. GÖREV TAMAMLANMA (0-10 puan) ──
  if (tasks.length > 0) {
    const completedTasks = tasks.filter(t => t.status === 'Abgeschlossen');
    const completionRate = completedTasks.length / tasks.length;
    score += Math.round(completionRate * 7);

    // Aktif görev varsa ek bonus
    const openTasks = tasks.filter(t => t.status === 'Offen');
    if (openTasks.length > 0) score += 3;
  }

  // ── 5. RECENCY DECAY (-15 ile 0 puan) ──
  if (lead.last_activity_date) {
    const daysSinceActivity = (Date.now() - new Date(lead.last_activity_date).getTime()) / (1000 * 60 * 60 * 24);
    if (daysSinceActivity > 90) score -= 15;
    else if (daysSinceActivity > 60) score -= 10;
    else if (daysSinceActivity > 30) score -= 5;
  } else if (normalizeLeadStatus(lead.status) !== 'Potenzieller Interessent') {
    // Hiç aktivite kaydı yok ve yeni lead değil -> ceza
    score -= 8;
  }

  // ── 6. MÜŞTERI DURUMU (0-10 puan) ──
  const leadStatus = normalizeLeadStatus(lead.status);
  if (leadStatus === 'Kunde') score += 10;
  else if (leadStatus === 'Interessenten') score += 5;
  else if (leadStatus === 'Verlorene Kunden') score -= 5;

  // ── 7. BÖLGE KÜMELENMESİ (0-10 puan) ──
  const sameAreaCustomers = existingLeads.filter(
    existing => existing.id !== lead.id && existing.plz === lead.plz && normalizeLeadStatus(existing.status) === 'Kunde'
  );
  score += Math.min(sameAreaCustomers.length * 3, 10);

  // ── 8. SEKTÖR BAŞARI ORANI (0-5 puan) ──
  const sameBrancheCustomers = existingLeads.filter(
    existing => existing.id !== lead.id && existing.branche === lead.branche && normalizeLeadStatus(existing.status) === 'Kunde'
  );
  score += Math.min(sameBrancheCustomers.length * 2, 5);

  // Skor sınırlandırma
  return Math.max(0, Math.min(100, Math.round(score)));
}

/**
 * Skora göre kategori belirler
 * @param {number} score - Lead skoru
 * @returns {string} Kategori adı
 */
export function categorizeByScore(score) {
  if (score >= 80) return 'Hohe Priorität';
  if (score >= 65) return 'Bestandskunden-Nähe';
  if (score >= 50) return 'Neue Branche';
  return 'Standard';
}

/**
 * Yeni lead için otomatik kategori belirler
 * @param {Object} lead - Lead objesi
 * @param {Array} existingLeads - Mevcut lead listesi
 * @returns {string} Kategori adı
 */
export function categorizeNewLead(lead, existingLeads = []) {
  const score = calculateLeadScore(lead, existingLeads);
  return categorizeByScore(score);
}

/**
 * Duplikat lead kontrolü yapar
 * @param {Object} newLead - Yeni lead
 * @param {Array} existingLeads - Mevcut lead listesi
 * @returns {Object|null} Duplikat lead veya null
 */
export function findDuplicateLead(newLead, existingLeads = []) {
  return existingLeads.find(existing => {
    // Google Place ID eşleşmesi (en güvenilir)
    if (existing.google_place_id && newLead.google_place_id &&
      !existing.google_place_id.startsWith('mock_') &&
      !newLead.google_place_id.startsWith('mock_') &&
      !existing.google_place_id.startsWith('scraped_') &&
      !newLead.google_place_id.startsWith('scraped_')) {
      if (existing.google_place_id === newLead.google_place_id) return true;
    }

    // İsim benzerliği kontrolü
    const nameStr1 = existing.name?.toLowerCase().trim();
    const nameStr2 = newLead.name?.toLowerCase().trim();
    const nameMatch = !!nameStr1 && !!nameStr2 && nameStr1 === nameStr2;

    // Adres benzerliği kontrolü - Boş string veya undefined'ların match olmasını engelle
    const strasse1 = existing.strasse?.toLowerCase().trim();
    const strasse2 = newLead.strasse?.toLowerCase().trim();
    const addressMatch = !!strasse1 && !!strasse2 && strasse1 === strasse2;

    const plzMatch = !!existing.plz && !!newLead.plz && existing.plz === newLead.plz;

    // Telefon eşleşmesi (boşlukları silerek)
    const phoneMatch = !!existing.telefon && !!newLead.telefon &&
      existing.telefon.replace(/\s+/g, '') === newLead.telefon.replace(/\s+/g, '');

    // Email eşleşmesi
    const emailMatch = !!existing.email && !!newLead.email &&
      existing.email.toLowerCase().trim() === newLead.email.toLowerCase().trim();

    // Aynı firma demek için:
    // Telefon aynı, Email aynı VEYA (İsim aynı VE (Adres aynı VEYA PostaKodu aynı))
    // Not: Sadece adresin aynı olması (aynı AVM'deki komşu 2 lokanta)
    // veya sadece ismin aynı olması (başka şehirdeki 2 şube) tek başına duplikat SAYILMAZ.
    return phoneMatch || emailMatch || (nameMatch && (addressMatch || plzMatch));
  }) || null;
}

/**
 * Kategori için renk sınıfı döndürür
 * @param {string} category - Kategori adı
 * @returns {string} Tailwind CSS sınıfları
 */
export function getCategoryColor(category) {
  const colors = {
    'Hohe Priorität': 'bg-red-100 text-red-700 border-red-300',
    'Bestandskunden-Nähe': 'bg-amber-100 text-amber-700 border-amber-300',
    'Neue Branche': 'bg-blue-100 text-blue-700 border-blue-300',
    'Standard': 'bg-slate-100 text-slate-600 border-slate-300',
  };
  return colors[category] || colors['Standard'];
}

/**
 * Kategori için ikon döndürür
 * @param {string} category - Kategori adı
 * @returns {string} Emoji ikon
 */
export function getCategoryIcon(category) {
  const icons = {
    'Hohe Priorität': '🔥',
    'Bestandskunden-Nähe': '📍',
    'Neue Branche': '🆕',
    'Standard': '📋',
  };
  return icons[category] || icons['Standard'];
}

/**
 * Skor için renk sınıfı döndürür
 * @param {number} score - Lead skoru
 * @returns {string} Tailwind CSS sınıfları
 */
export function getScoreColor(score) {
  if (score >= 80) return 'text-emerald-600';
  if (score >= 65) return 'text-blue-600';
  if (score >= 50) return 'text-amber-600';
  return 'text-slate-500';
}

export default {
  calculateLeadScore,
  categorizeByScore,
  categorizeNewLead,
  findDuplicateLead,
  getCategoryColor,
  getCategoryIcon,
  getScoreColor,
};
